/**
 * Location SearchableSelect - VRP System v4
 * Enhanced mobile UX and accessibility with VRP design system compliance
 */
import * as React from 'react'
import { MapPin, Plus, Loader2 } from 'lucide-react'
import {
  SearchableSelect,
  type SearchableSelectOption,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'
import { Badge } from './badge'
import type { Doc } from '../../../convex/_generated/dataModel'

interface LocationSelectProps {
  value: string | undefined
  onValueChange: (value: string) => void
  locations: Doc<'locations'>[]
  className?: string
  noLocationValue?: string
  onCreateNew?: () => void
  isLoading?: boolean
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  autoOpen?: boolean // Auto-open dropdown on mount for immediate interaction
  'aria-label'?: string
  'aria-describedby'?: string
}

// Enhanced location type badge mapping for better UX
const getLocationTypeBadgeVariant = (locationType?: string) => {
  switch (locationType?.toLowerCase()) {
    case 'depot':
    case 'warehouse':
      return 'default'
    case 'customer':
    case 'delivery':
      return 'secondary'
    case 'pickup':
      return 'info'
    case 'service':
      return 'success'
    default:
      return 'outline'
  }
}

// Transform location data to SearchableSelect format with enhanced accessibility
const transformLocations = (
  locations: Doc<'locations'>[] | undefined,
  noLocationValue?: string
): SearchableSelectOption<Doc<'locations'>>[] => {
  // Handle undefined, null, or empty locations array
  if (!locations || !Array.isArray(locations)) {
    return []
  }

  return locations.map(location => ({
    value: location._id,
    label: location.name,
    keywords: [
      location.name,
      location.address || '',
      location.locationType || '',
      location.description || '',
      // Add coordinate search capability
      location.locationLat?.toString() || '',
      location.locationLon?.toString() || '',
    ].filter(Boolean),
    icon: (
      <MapPin className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
    ),
    badge: location.locationType || undefined,
    data: location,
  }))
}

// Enhanced error boundary fallback with better accessibility and mobile UX
const LocationSelectFallback: React.FC<LocationSelectProps> = ({
  value,
  onValueChange,
  locations,
  className,
  noLocationValue = 'NO_LOCATION_VALUE',
  onCreateNew,
  isLoading = false,
  placeholder = 'Select location...',
  disabled = false,
  autoOpen = false, // eslint-disable-line @typescript-eslint/no-unused-vars -- Not used in fallback Select but kept for prop consistency
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}) => (
  <Select
    value={value || noLocationValue}
    onValueChange={onValueChange}
    disabled={disabled || isLoading}
  >
    <SelectTrigger
      className={className}
      aria-label={ariaLabel || 'Select a location'}
      aria-describedby={ariaDescribedBy}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {isLoading ? (
          <Loader2
            className="w-4 h-4 animate-spin text-muted-foreground"
            aria-hidden="true"
          />
        ) : (
          <MapPin
            className="w-4 h-4 text-muted-foreground shrink-0"
            aria-hidden="true"
          />
        )}
        <SelectValue placeholder={placeholder} />
      </div>
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={noLocationValue}>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4" aria-hidden="true" />
          <span>No location</span>
        </div>
      </SelectItem>
      {locations && Array.isArray(locations)
        ? locations.map(location => (
            <SelectItem key={location._id} value={location._id}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <MapPin
                  className="w-4 h-4 text-muted-foreground shrink-0"
                  aria-hidden="true"
                />
                <span className="truncate">{location.name}</span>
                {location.locationType && (
                  <Badge
                    variant={
                      getLocationTypeBadgeVariant(location.locationType) as any
                    }
                    className="text-sm shrink-0"
                  >
                    {location.locationType}
                  </Badge>
                )}
              </div>
            </SelectItem>
          ))
        : null}
      {onCreateNew && !isLoading && (
        <SelectItem value="__create_new__" onClick={onCreateNew}>
          <div className="flex items-center gap-2 text-primary">
            <Plus className="w-4 h-4" aria-hidden="true" />
            <span>Create New Location</span>
          </div>
        </SelectItem>
      )}
    </SelectContent>
  </Select>
)

export const LocationSelect: React.FC<LocationSelectProps> = ({
  value,
  onValueChange,
  locations,
  className,
  noLocationValue,
  onCreateNew,
  isLoading = false,
  placeholder = 'Select location...',
  searchPlaceholder = 'Search locations...',
  emptyMessage = 'No locations found',
  disabled = false,
  autoOpen = false,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  ...props
}) => {
  // Enhanced location options with improved accessibility
  const options = React.useMemo(
    () => transformLocations(locations),
    [locations]
  )

  // Enhanced error boundary with performance monitoring
  const [hasError, setHasError] = React.useState(false)

  React.useEffect(() => {
    // Reset error state when locations change
    if (hasError && locations && locations.length > 0) {
      setHasError(false)
    }
  }, [locations, hasError])

  // Enhanced create new handler with better UX
  const handleCreateNew = React.useCallback(() => {
    if (onCreateNew) {
      try {
        onCreateNew()
      } catch (error) {
        console.warn('Error creating new location:', error)
        setHasError(true)
      }
    }
  }, [onCreateNew])

  // Enhanced value change handler with validation
  const handleValueChange = React.useCallback(
    (newValue: string) => {
      try {
        if (newValue === '__create_new__') {
          handleCreateNew()
          return
        }
        onValueChange(newValue)
      } catch (error) {
        console.warn('Error changing location value:', error)
        setHasError(true)
      }
    },
    [onValueChange, handleCreateNew]
  )

  // Use fallback if there are errors or SearchableSelect is not available
  if (hasError) {
    return (
      <LocationSelectFallback
        value={value}
        onValueChange={onValueChange}
        locations={locations}
        className={className}
        noLocationValue={noLocationValue}
        onCreateNew={onCreateNew}
        isLoading={isLoading}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        {...props}
      />
    )
  }

  try {
    return (
      <SearchableSelect
        value={value}
        onValueChange={handleValueChange}
        options={options}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        className={className}
        noSelectionValue={noLocationValue}
        noSelectionLabel="No location"
        createNewValue="__create_new__"
        createNewLabel="Create New Location"
        onCreateNew={handleCreateNew}
        isLoading={isLoading}
        autoOpen={autoOpen}
        // Enhanced accessibility props
        aria-label={ariaLabel || 'Select a location from the list'}
        aria-describedby={ariaDescribedBy}
        // Performance optimizations
        maxHeight={400} // Larger on mobile for better UX
        {...(disabled && { 'aria-disabled': true })}
        {...props}
      />
    )
  } catch (error) {
    console.warn('SearchableSelect failed, using fallback:', error)
    setHasError(true)
    return (
      <LocationSelectFallback
        value={value}
        onValueChange={onValueChange}
        locations={locations}
        className={className}
        noLocationValue={noLocationValue}
        onCreateNew={onCreateNew}
        isLoading={isLoading}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        {...props}
      />
    )
  }
}

// Enhanced location select with preloading capability for better mobile performance
export const LocationSelectWithPreload: React.FC<
  LocationSelectProps & {
    preloadLocations?: boolean
    onPreloadComplete?: () => void
  }
> = ({ preloadLocations = false, onPreloadComplete, locations, ...props }) => {
  const [isPreloading, setIsPreloading] = React.useState(preloadLocations)

  React.useEffect(() => {
    if (preloadLocations && locations && locations.length > 0) {
      // Simulate preloading process for large datasets
      const timer = setTimeout(() => {
        setIsPreloading(false)
        onPreloadComplete?.()
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [preloadLocations, locations.length, onPreloadComplete])

  return (
    <LocationSelect
      {...props}
      locations={locations}
      isLoading={props.isLoading || isPreloading}
    />
  )
}

export default LocationSelect
